import React from 'react';
import { osc, lerp, clamp, easeOut, drawOn } from '../../core/AnimationUtils';

/*
 * Coding concept-acting SCENES (Anivaze v2) — programming concepts are played
 * out in front of you rather than being labelled in diagrams.
 * Kindergarten-friendly, pure function of `t`.  See design-plan/v2/ANIMATION-PATTERN.md.
 */

/* ─── Local helpers ──────────────────────────────────────────────────────────── */

/** A rounded-rect "card" with a label — used for command cards and loop blocks. */
const Card = (
  x: number, y: number, w: number, h: number,
  text: string, bg: string, border: string, textColor: string,
  fontSize = 14, rx = 10,
) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx={rx} fill={bg} stroke={border} strokeWidth={2.5} />
    <text x={x + w / 2} y={y + h / 2 + 5} textAnchor="middle"
      fill={textColor} fontSize={fontSize} fontWeight={700}
      fontFamily="Inter,sans-serif">{text}</text>
  </g>
);

/** Cute blocky robot face, centred at (cx, cy). eyeOpen 0→1. */
const Robot = (cx: number, cy: number, eyeOpen: number, accent: string) => {
  const eo = clamp(eyeOpen, 0.1, 1);
  return (
    <g>
      {/* shadow */}
      <ellipse cx={cx} cy={cy + 44} rx={28} ry={7} fill="rgba(0,0,0,0.18)" />
      {/* body */}
      <rect x={cx - 22} y={cy + 14} width={44} height={32} rx={8} fill={accent} stroke="#1e3a5f" strokeWidth={2} />
      {/* antenna */}
      <line x1={cx} y1={cy - 26} x2={cx} y2={cy - 36} stroke="#64748b" strokeWidth={3} strokeLinecap="round" />
      <circle cx={cx} cy={cy - 38} r={5} fill="#fbbf24" stroke="#b45309" strokeWidth={1.5} />
      {/* head */}
      <rect x={cx - 24} y={cy - 26} width={48} height={42} rx={10} fill={accent} stroke="#1e3a5f" strokeWidth={2} />
      {/* eyes */}
      <ellipse cx={cx - 9} cy={cy - 8} rx={6} ry={6 * eo} fill="#fff" />
      <ellipse cx={cx + 9} cy={cy - 8} rx={6} ry={6 * eo} fill="#fff" />
      <circle cx={cx - 9} cy={cy - 8} r={3 * eo} fill="#1e3a5f" />
      <circle cx={cx + 9} cy={cy - 8} r={3 * eo} fill="#1e3a5f" />
      {/* mouth */}
      <path d={`M${cx - 8},${cy + 4} Q${cx},${cy + 10} ${cx + 8},${cy + 4}`}
        fill="none" stroke="#1e3a5f" strokeWidth={2} strokeLinecap="round" />
      {/* chest panel */}
      <rect x={cx - 12} y={cy + 20} width={24} height={14} rx={4} fill="#bae6fd" stroke="#7dd3fc" strokeWidth={1.5} />
      {Array.from({ length: 3 }, (_, k) => (
        <circle key={k} cx={cx - 8 + k * 8} cy={cy + 27} r={3}
          fill={k === 1 ? '#22c55e' : '#94a3b8'} />
      ))}
    </g>
  );
};

/* ━━━ CodingSequenceScene: robot follows command cards one at a time ━━━━━━━━━ */
export interface CodingSequenceSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** seconds since scene started, pure-function input */
  t?: number;
}

export const CodingSequenceScene: React.FC<CodingSequenceSceneProps> = ({
  opacity = 1, accentColor = '#3b82f6', t = 0,
  label = 'Code is just instructions, one step at a time!',
}) => {
  /* ── layout constants ─────────────────────────────────────────────────────── */
  const LOOP_DUR = 8;          // seconds per full loop
  const COMMANDS = ['Move →', 'Move →', 'Move ↑', 'Grab 🍪'] as const;
  const NUM_CMDS = COMMANDS.length;
  const CMD_HOLD = 1.4;        // seconds per command (NUM_CMDS × CMD_HOLD < LOOP_DUR)
  const RESET_DUR = LOOP_DUR - NUM_CMDS * CMD_HOLD; // ~2.4s — robot slides back

  /* Grid: 5 cols × 4 rows, top-left = (320,120) */
  const CELL = 60;
  const GRID_COLS = 5;
  const GRID_ROWS = 4;
  const GX0 = 310;   // grid origin x
  const GY0 = 110;   // grid origin y

  /* Robot start + end on grid (col, row), path mirrors commands */
  const START_COL = 1, START_ROW = 2;
  // Move→, Move→, Move↑ takes it to col 3, row 1 then Grab at (3,1)
  const PATH: Array<[number, number]> = [
    [START_COL,     START_ROW    ],   // after cmd 0 (start → after Move→)
    [START_COL + 1, START_ROW    ],   // after cmd 1
    [START_COL + 2, START_ROW    ],   // after cmd 2 (Move→ again)
    [START_COL + 2, START_ROW - 1],   // after cmd 3 (Move↑)
  ];
  const GOAL_COL = START_COL + 2;
  const GOAL_ROW = START_ROW - 1;

  /* ── timing ────────────────────────────────────────────────────────────────── */
  const phase = (t % LOOP_DUR);                         // 0 … LOOP_DUR

  // Which command slot are we in? -1 means reset phase.
  const cmdPhase = phase < NUM_CMDS * CMD_HOLD ? phase : -1;
  const activeCmd = cmdPhase >= 0 ? Math.floor(cmdPhase / CMD_HOLD) : -1;
  const cmdProgress = cmdPhase >= 0 ? (cmdPhase % CMD_HOLD) / CMD_HOLD : 0;

  // Robot grid position — moves exactly when cmdProgress crosses the mid-point
  const completedCmds = activeCmd >= 0
    ? activeCmd + (cmdProgress > 0.5 ? 1 : 0)
    : NUM_CMDS;

  // During reset we slide back to start
  const resetProgress = cmdPhase < 0
    ? clamp((phase - NUM_CMDS * CMD_HOLD) / RESET_DUR, 0, 1)
    : 0;

  // Lerp from the completed-step position back to the start for reset
  const [curCol, curRow] = PATH[Math.min(completedCmds, NUM_CMDS - 1)];
  const robotCol = resetProgress > 0
    ? lerp(curCol, START_COL, easeOut(resetProgress))
    : lerp(
        completedCmds === 0 ? START_COL : PATH[completedCmds - 1][0],
        curCol,
        easeOut(clamp(cmdProgress * 2, 0, 1)),
      );
  const robotRow = resetProgress > 0
    ? lerp(curRow, START_ROW, easeOut(resetProgress))
    : lerp(
        completedCmds === 0 ? START_ROW : PATH[completedCmds - 1][1],
        curRow,
        easeOut(clamp(cmdProgress * 2, 0, 1)),
      );

  const robotX = GX0 + (robotCol + 0.5) * CELL;
  const robotY = GY0 + (robotRow + 0.5) * CELL + 10;

  // Cookie / star "grab" effect
  const isGrabbing = activeCmd === 3 && cmdProgress > 0.55;
  const cookieScale = isGrabbing ? 1 + osc(t, 0.3, 0, 0.25) : 1;

  /* ── robot eye blink (purely cosmetic) ─────────────────────────────────────── */
  const eyeOpen = osc(t, 4, 0, 1) > 0.92 ? clamp(1 - (osc(t, 4, 0, 1) - 0.92) / 0.08 * 2, 0.1, 1) : 1;

  /* ── card panel (left side) ─────────────────────────────────────────────────── */
  const CARD_X = 28, CARD_W = 110, CARD_H = 42, CARD_GAP = 10;
  const CARD_LABEL_FONT = 13;

  return (
    <g opacity={opacity}>
      {/* Sky background */}
      <defs>
        <linearGradient id="csc-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#dbeafe" />
          <stop offset="100%" stopColor="#eff6ff" />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={800} height={450} fill="url(#csc-bg)" />

      {/* ── grid ──────────────────────────────────────────────────────────────── */}
      {Array.from({ length: GRID_ROWS }, (_, r) =>
        Array.from({ length: GRID_COLS }, (_, c) => {
          const isPath = PATH.some(([pc, pr]) => pc === c && pr === r);
          const isStart = c === START_COL && r === START_ROW;
          const isGoal  = c === GOAL_COL  && r === GOAL_ROW;
          return (
            <rect key={`${r}-${c}`}
              x={GX0 + c * CELL + 3} y={GY0 + r * CELL + 3}
              width={CELL - 6} height={CELL - 6} rx={8}
              fill={isGoal ? '#fef9c3' : isStart ? '#dcfce7' : isPath ? '#eff6ff' : '#f1f5f9'}
              stroke={isGoal ? '#fbbf24' : isPath ? '#bfdbfe' : '#cbd5e1'}
              strokeWidth={2}
            />
          );
        })
      )}

      {/* Footprint trail dots */}
      {PATH.slice(0, completedCmds).map(([pc, pr], idx) => (
        <circle key={idx}
          cx={GX0 + (pc + 0.5) * CELL} cy={GY0 + (pr + 0.5) * CELL + 10}
          r={5} fill="#93c5fd" opacity={0.6} />
      ))}

      {/* Cookie / star goal */}
      <g transform={`translate(${GX0 + (GOAL_COL + 0.5) * CELL} ${GY0 + (GOAL_ROW + 0.5) * CELL + 10}) scale(${cookieScale})`}>
        <text textAnchor="middle" dominantBaseline="middle" fontSize={26}>🍪</text>
      </g>

      {/* ── command cards panel ───────────────────────────────────────────────── */}
      {/* Panel background */}
      <rect x={16} y={80} width={CARD_W + 24} height={NUM_CMDS * (CARD_H + CARD_GAP) + CARD_GAP + 30}
        rx={14} fill="rgba(255,255,255,0.82)" stroke="#bfdbfe" strokeWidth={2} />
      <text x={CARD_X + CARD_W / 2} y={105} textAnchor="middle"
        fill="#1d4ed8" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif">PROGRAM</text>

      {COMMANDS.map((cmd, i) => {
        const isActive = i === activeCmd;
        const isDone   = completedCmds > i && resetProgress === 0;
        const bg     = isActive ? '#fef9c3' : isDone ? '#dcfce7' : '#fff';
        const border = isActive ? '#f59e0b' : isDone ? '#22c55e' : '#cbd5e1';
        const tcolor = isActive ? '#92400e' : isDone ? '#166534' : '#475569';
        const cy = 115 + i * (CARD_H + CARD_GAP);
        const scaleY = isActive ? 1 + osc(t, 0.35, 0, 0.06) : 1;

        return (
          <g key={i} transform={`translate(${CARD_X + CARD_W / 2} ${cy + CARD_H / 2}) scale(1 ${scaleY}) translate(${-(CARD_X + CARD_W / 2)} ${-(cy + CARD_H / 2)})`}>
            {Card(CARD_X, cy, CARD_W, CARD_H, cmd, bg, border, tcolor, CARD_LABEL_FONT)}
            {isDone && (
              <text x={CARD_X + CARD_W - 14} y={cy + CARD_H / 2 + 5}
                textAnchor="middle" fontSize={13} fill="#22c55e">✓</text>
            )}
            {isActive && (
              /* Arrow pointer */
              <polygon points={`${CARD_X - 12},${cy + CARD_H / 2 - 6} ${CARD_X - 2},${cy + CARD_H / 2} ${CARD_X - 12},${cy + CARD_H / 2 + 6}`}
                fill="#f59e0b" />
            )}
          </g>
        );
      })}

      {/* Step counter badge */}
      <g transform="translate(200 78)">
        <rect x={-38} y={-16} width={76} height={28} rx={10}
          fill={activeCmd >= 0 ? '#fef9c3' : '#f1f5f9'} stroke="#e2e8f0" strokeWidth={1.5} />
        <text x={0} y={5} textAnchor="middle" fill="#475569"
          fontSize={12} fontWeight={600} fontFamily="Inter,sans-serif">
          {activeCmd >= 0 ? `Step ${activeCmd + 1} of ${NUM_CMDS}` : 'Resetting…'}
        </text>
      </g>

      {/* ── robot ─────────────────────────────────────────────────────────────── */}
      {Robot(robotX, robotY, eyeOpen, accentColor)}

      {/* ── caption ───────────────────────────────────────────────────────────── */}
      <g transform="translate(400 424)">
        <rect x={-268} y={-18} width={536} height={34} rx={17}
          fill="rgba(255,255,255,0.92)" stroke="#bfdbfe" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle"
          fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ CodingLoopScene: a loop block makes a character jump 3×, then check ━━━ */
export interface CodingLoopSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** seconds since scene started, pure-function input */
  t?: number;
}

export const CodingLoopScene: React.FC<CodingLoopSceneProps> = ({
  opacity = 1, accentColor = '#8b5cf6', t = 0,
  label = 'A loop repeats steps for you!',
}) => {
  /* ── timing ────────────────────────────────────────────────────────────────── */
  const LOOP_DUR = 6;           // total loop cycle seconds
  const NUM_JUMPS = 3;
  const JUMP_HOLD = 1.2;        // seconds each jump takes
  const DONE_PHASE = NUM_JUMPS * JUMP_HOLD;  // 3.6s
  const CHECK_DUR = 1.0;
  // remaining ~1.4s is pause before loop reset — no explicit variable needed

  const phase = t % LOOP_DUR;  // 0 … LOOP_DUR

  // Which jump are we on?
  const inJumps = phase < DONE_PHASE;
  const inCheck = !inJumps && phase < DONE_PHASE + CHECK_DUR;
  const jumpIdx = inJumps ? Math.floor(phase / JUMP_HOLD) : NUM_JUMPS - 1;
  const jumpProgress = inJumps ? (phase % JUMP_HOLD) / JUMP_HOLD : 0;

  // Squash-stretch jump arc (0 = ground, peak at 0.5)
  const jumpArc   = inJumps ? Math.sin(clamp(jumpProgress, 0, 1) * Math.PI) : 0;
  const charLift  = jumpArc * 70;                       // pixels up
  const scaleX    = inJumps ? 1 - jumpArc * 0.18 : 1;  // squash horizontally at peak
  const scaleY    = inJumps ? 1 + jumpArc * 0.22 : 1;  // stretch vertically at peak
  const shadowScale = 1 - jumpArc * 0.55;

  // Completed jumps count (increments after each JUMP_HOLD)
  const completedJumps = inJumps
    ? jumpIdx + (jumpProgress > 0.9 ? 1 : 0)
    : NUM_JUMPS;

  /* ── character position ─────────────────────────────────────────────────────── */
  const charCX = 310;
  const charFloorY = 285;
  const charY = charFloorY - charLift;

  /* ── loop-block card ─────────────────────────────────────────────────────────── */
  const blockX = 460, blockY = 160, blockW = 240, blockH = 110;
  // inner command line
  const innerX = blockX + 20, innerY = blockY + 54, innerW = blockW - 40, innerH = 36;

  /* ── "verbose" ghost labels (Jump × 3 shown faintly to contrast with loop) ── */
  const verboseX = 100;

  /* ── check-mark alpha ─────────────────────────────────────────────────────── */
  const checkAlpha = inCheck
    ? drawOn(phase - DONE_PHASE, CHECK_DUR * 0.6)
    : 0;

  /* ── counter pulse on each new jump ─────────────────────────────────────────── */
  const counterPulse = inJumps ? 1 + jumpArc * 0.3 : 1;

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="clc-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f5f3ff" />
          <stop offset="100%" stopColor="#ede9fe" />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={800} height={450} fill="url(#clc-bg)" />

      {/* Floor */}
      <rect x={0} y={310} width={800} height={140} rx={0} fill="#d1fae5" />
      <rect x={0} y={310} width={800} height={6} fill="#6ee7b7" />

      {/* ── verbose side (left): "Do it yourself = 3 separate cards" ──────────── */}
      <g opacity={0.38}>
        <rect x={verboseX - 30} y={150} width={140} height={160} rx={12}
          fill="#fff" stroke="#c4b5fd" strokeWidth={1.5} />
        <text x={verboseX + 40} y={173} textAnchor="middle"
          fill="#7c3aed" fontSize={10} fontWeight={700} fontFamily="Inter,sans-serif">Without loop</text>
        {(['Jump', 'Jump', 'Jump'] as const).map((lbl, i) => (
          <g key={i}>{Card(verboseX - 16, 182 + i * 38, 112, 28, lbl,
            '#f5f3ff', '#c4b5fd', '#6d28d9', 11, 7)}</g>
        ))}
      </g>

      {/* Divider */}
      <line x1={248} y1={150} x2={248} y2={310} stroke="#a78bfa" strokeWidth={2} strokeDasharray="5 5" opacity={0.5} />
      <text x={260} y={230} fill="#a78bfa" fontSize={11} fontWeight={700}
        fontFamily="Inter,sans-serif" transform="rotate(-90,260,230)">vs</text>

      {/* ── loop block (right panel) ──────────────────────────────────────────── */}
      {/* Outer "Repeat" block */}
      <rect x={blockX} y={blockY} width={blockW} height={blockH} rx={14}
        fill="#7c3aed" stroke="#6d28d9" strokeWidth={2.5} />
      <text x={blockX + blockW / 2} y={blockY + 26} textAnchor="middle"
        fill="#fff" fontSize={15} fontWeight={800} fontFamily="Inter,sans-serif">
        Repeat 3×
      </text>

      {/* Inner command */}
      <rect x={innerX} y={innerY} width={innerW} height={innerH} rx={8}
        fill="#ede9fe" stroke="#c4b5fd" strokeWidth={2} />
      <text x={innerX + innerW / 2} y={innerY + innerH / 2 + 5} textAnchor="middle"
        fill="#4c1d95" fontSize={14} fontWeight={700} fontFamily="Inter,sans-serif">
        Jump
      </text>

      {/* Counter badge (shows current repeat number) */}
      <g transform={`translate(${blockX + blockW - 26} ${blockY + 26}) scale(${counterPulse})`}>
        <circle r={18} fill={completedJumps >= NUM_JUMPS && !inJumps ? '#22c55e' : '#fbbf24'}
          stroke="#fff" strokeWidth={2.5} />
        <text x={0} y={6} textAnchor="middle"
          fill="#1c1917" fontSize={14} fontWeight={800} fontFamily="Inter,sans-serif">
          {Math.min(completedJumps + (inJumps && jumpProgress > 0.05 ? 1 : 0), NUM_JUMPS)}
        </text>
      </g>

      {/* Jump dots (one per completed jump) */}
      {Array.from({ length: NUM_JUMPS }, (_, k) => {
        const done = completedJumps > k || (inJumps && jumpIdx === k && jumpProgress > 0.55);
        return (
          <circle key={k}
            cx={blockX + 30 + k * 28} cy={blockY + blockH + 22}
            r={8}
            fill={done ? '#22c55e' : '#e2e8f0'}
            stroke={done ? '#16a34a' : '#cbd5e1'}
            strokeWidth={2} />
        );
      })}

      {/* Green check */}
      {checkAlpha > 0 && (
        <g transform={`translate(${blockX + blockW / 2} ${blockY - 36})`} opacity={checkAlpha}>
          <circle r={22} fill="#22c55e" stroke="#16a34a" strokeWidth={2.5} />
          <text x={0} y={8} textAnchor="middle" fontSize={22} fill="#fff">✓</text>
        </g>
      )}

      {/* ── character (centre-left of scene) ─────────────────────────────────── */}
      {/* Shadow on floor */}
      <ellipse cx={charCX} cy={charFloorY + 34}
        rx={28 * shadowScale} ry={6 * shadowScale}
        fill="rgba(0,0,0,0.18)" />

      {/* Squash-stretch wrapper */}
      <g transform={`translate(${charCX} ${charY}) scale(${scaleX} ${scaleY}) translate(${-charCX} ${-charY})`}>
        {Robot(charCX, charY, 1, accentColor)}
      </g>

      {/* "Jump!" speech bubble when in air */}
      {jumpArc > 0.2 && (
        <g transform={`translate(${charCX + 42} ${charY - 30})`}>
          <rect x={-24} y={-16} width={68} height={26} rx={10}
            fill="#fef9c3" stroke="#fbbf24" strokeWidth={1.5} />
          <polygon points="-10,-4 -22,4 -5,4" fill="#fef9c3" stroke="#fbbf24" strokeWidth={1} />
          <text x={10} y={5} textAnchor="middle"
            fill="#92400e" fontSize={13} fontWeight={800} fontFamily="Inter,sans-serif">Jump!</text>
        </g>
      )}

      {/* ── caption ───────────────────────────────────────────────────────────── */}
      <g transform="translate(400 424)">
        <rect x={-218} y={-18} width={436} height={34} rx={17}
          fill="rgba(255,255,255,0.92)" stroke="#ddd6fe" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle"
          fill="#0f172a" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
